import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../../../stores/types/types';
import { SaveSegment, LoadSegments, DeleteSegment, Segment } from '../../../connection/segments';
import {
	StyledButton,
	StyledLabel,
	StyledLi,
	StyledUl,
	HeaderContainer,
	StyledInput,
} from './debug-ui';
import { showAlert } from '../../general-components/alert-service';
import { ALERT_TYPES } from '../../general-components/alert-types';

export default function DebugSegments() {
	const meeting = useSelector((s: GreenroomState) => s.SessionReducer.meeting);

	const [segments, setSegments] = useState<Segment[]>([]);
	const [name, setName] = useState<string>('Test Segment');
	const [color, setColor] = useState<string>('#FF0000');
	const [duration, setDuration] = useState<number>(60);
	const [orderIndex, setOrderIndex] = useState<number | null>(null);
	const [selectedId, setSelectedId] = useState<number>(0);

	const handleLoad = async () => {
		if (!meeting) return;
		const result = await LoadSegments(meeting.meeting);
		if (result) {
			setSegments(result);
		} else {
			showAlert({
				message: 'Error',
				description: 'Failed to load segments',
				duration: 4000,
				type: ALERT_TYPES.GENERAL_ERROR,
			});
		}
	};

	const handleUpsert = async () => {
		if (!meeting) return;

		const newSegment: Partial<Segment> = {
			id: selectedId,
			name,
			color,
			duration, 
			orderIndex
		};

		const result = await SaveSegment(meeting.meeting, newSegment);
		if (typeof result === 'string' || !result) {
			showAlert({
				message: 'Error',
				description: `Failed to upsert segment: ${result ?? ''}`,
				duration: 4000,
				type: ALERT_TYPES.GENERAL_ERROR,
			});
		} else {
			showAlert({
				message: 'Success',
				description: `Upserted segment: ${result.name}`,
				duration: 2000,
				type: ALERT_TYPES.INFO
			});
			handleLoad();
		}
	};

	const handleDelete = async () => {
		if (!meeting || !selectedId) return;
		const result = await DeleteSegment(meeting.meeting, selectedId);
		if (typeof result === 'string' || !result) {
			showAlert({
				message: 'Error',
				description: 'Failed to delete segment',
				duration: 4000,
				type: ALERT_TYPES.GENERAL_ERROR,
			});
		} else {
			showAlert({
				message: 'Success',
				description: 'Deleted segment',
				duration: 4000,
				type: ALERT_TYPES.INFO
			});
			setSelectedId(0);
			handleLoad();
		}
	};

	return (
		<div className="section settings">
			<HeaderContainer>
				<h5>Segments CRUD Test</h5>
			</HeaderContainer>
			<div className="advanced-settings-container">
				<StyledUl>
					<StyledLi>
						<StyledLabel>ID (for update/delete):</StyledLabel>
						<StyledInput
							type="number"
							value={selectedId}
							onChange={(e) => setSelectedId(parseInt(e.target.value) || 0)}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>Name:</StyledLabel>
						<StyledInput
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>Color:</StyledLabel>
						<StyledInput
							type="text"
							value={color}
							onChange={(e) => setColor(e.target.value)}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>Duration:</StyledLabel>
						<StyledInput
							type="number"
							value={duration}
							onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>Order Index:</StyledLabel>
						<StyledInput
							type="number"
							value={orderIndex ?? 'null'}
							onChange={(e) => {
								const intVal = parseInt(e.target.value, 10);
								if (typeof intVal === 'number') {
									setOrderIndex(intVal);
								}
							}}
						/>
					</StyledLi>
					<StyledLi className="justify-content-space-between margin-top-small margin-bottom-small">
						<StyledButton
							className="background-green text-dark"
							onClick={handleLoad}
						>
							Load All
						</StyledButton>
						<StyledButton
							className="background-green text-dark"
							onClick={handleUpsert}
						>
							Upsert
						</StyledButton>
						<StyledButton
							className="background-red text-white"
							onClick={handleDelete}
							disabled={!selectedId}
						>
							Delete
						</StyledButton>
					</StyledLi>
				</StyledUl>

				<h6 className="bl-font-header">Loaded Segments:</h6>
				{segments.length > 0 && (
					<div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap' }}>
						{segments.map((seg) => (
							<StyledUl key={seg.id} style={{ marginTop: '8px', 
							}}>
									<div>Order: {seg.orderIndex}</div>
									
									<div>ID: {seg.id}</div>
									<div>{seg.name ?? 'Unnamed'}</div>
									<div>Duration: {seg.duration}</div>
									<div>Color: {seg.color}</div>
									
							</StyledUl>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
